import React, { useState, useRef } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import * as backgroundServices from "../../services/testimonial.services";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";

const BackgroundRemoval = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const poofFileInputRef = useRef(null);

    // ─── Picsart States ───────────────────────────────────────────
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState("");
    const [resultImage, setResultImage] = useState("");
    const [loading, setLoading] = useState(false);
    const [picsartApiKey, setPicsartApiKey] = useState("");
    const [processingTime, setProcessingTime] = useState(null);

    // ─── Poof States ──────────────────────────────────────────────
    const [poofSelectedFile, setPoofSelectedFile] = useState(null);
    const [poofPreviewImage, setPoofPreviewImage] = useState("");
    const [poofResultImage, setPoofResultImage] = useState("");
    const [poofLoading, setPoofLoading] = useState(false);
    const [poofApiKey, setPoofApiKey] = useState("");
    const [poofProcessingTime, setPoofProcessingTime] = useState(null);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Background Removal", url: "" },
    ];

    // ─── Picsart Handlers ─────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.currentTarget.files[0];
        if (!file) return;
        const supportedFormats = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        if (!supportedFormats.includes(file.type)) {
            Swal.fire({ icon: "error", text: "Only JPG, PNG, GIF, WEBP formats are supported.", ...SWAL_SETTINGS });
            return;
        }
        setSelectedFile(file);
        setPreviewImage(URL.createObjectURL(file));
        setResultImage("");
        setProcessingTime(null);
    };

    const handleRemove = () => {
        setSelectedFile(null);
        setPreviewImage("");
        setResultImage("");
        setProcessingTime(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            Swal.fire({ icon: "error", text: "Please select an image first.", ...SWAL_SETTINGS });
            return;
        }
        setLoading(true);
        setProcessingTime(null);
        const formData = new FormData();
        formData.append("image", selectedFile);
        if (picsartApiKey.trim()) {
            formData.append("picsart_api_key", picsartApiKey.trim());
        }
        try {
            const response = await backgroundServices.RemoveBackground(formData);
            if (response.success) {
                setResultImage(response.data.image);
                if (response.data.processing_time_sec) {
                    setProcessingTime(response.data.processing_time_sec);
                }
            } else {
                Swal.fire({ icon: "error", text: response.message || "Background removal failed.", ...SWAL_SETTINGS });
            }
        } catch (error) {
            Swal.fire({ icon: "error", text: "Something went wrong. Please try again.", ...SWAL_SETTINGS });
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!resultImage) return;
        const link = document.createElement("a");
        link.href = resultImage;
        link.download = `removed_bg_${Date.now()}.png`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ─── Poof Handlers ────────────────────────────────────────────
    const handlePoofFileChange = (e) => {
        const file = e.currentTarget.files[0];
        if (!file) return;
        const supportedFormats = ["image/jpeg", "image/png", "image/webp"];
        if (!supportedFormats.includes(file.type)) {
            Swal.fire({ icon: "error", text: "Only JPG, PNG, WEBP formats are supported.", ...SWAL_SETTINGS });
            return;
        }
        setPoofSelectedFile(file);
        setPoofPreviewImage(URL.createObjectURL(file));
        setPoofResultImage("");
        setPoofProcessingTime(null);
    };

    const handlePoofRemove = () => {
        setPoofSelectedFile(null);
        setPoofPreviewImage("");
        setPoofResultImage("");
        setPoofProcessingTime(null);
        if (poofFileInputRef.current) poofFileInputRef.current.value = "";
    };

    const handlePoofSubmit = async () => {
        if (!poofSelectedFile) {
            Swal.fire({ icon: "error", text: "Please select an image first.", ...SWAL_SETTINGS });
            return;
        }
        setPoofLoading(true);
        setPoofProcessingTime(null);
        const formData = new FormData();
        formData.append("image", poofSelectedFile);
        if (poofApiKey.trim()) {
            formData.append("poof_api_key", poofApiKey.trim());
        }
        try {
            const response = await backgroundServices.RemoveBackgroundPoof(formData);
            if (response.success) {
                setPoofResultImage(response.data.image);
                if (response.data.processing_time_sec) {
                    setPoofProcessingTime(response.data.processing_time_sec);
                }
            } else {
                Swal.fire({ icon: "error", text: response.message || "Background removal failed.", ...SWAL_SETTINGS });
            }
        } catch (error) {
            Swal.fire({ icon: "error", text: "Something went wrong. Please try again.", ...SWAL_SETTINGS });
        } finally {
            setPoofLoading(false);
        }
    };

    const handlePoofDownload = () => {
        if (!poofResultImage) return;
        const link = document.createElement("a");
        link.href = poofResultImage;
        link.download = `poof_removed_bg_${Date.now()}.png`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ─── Reusable Upload Card ─────────────────────────────────────
    const UploadCard = ({
        fileInputRef, accept, supportedText,
        apiKeyValue, apiKeyPlaceholder, onApiKeyChange,
        previewImage, processingTime,
        onFileChange, onSubmit, onReset,
        loading, selectedFile
    }) => (
        <div className="card custom-card h-100 shadow-sm border-0">
            <div className="card-body">
                <h6 className="mb-3">Upload Image</h6>

                {/* Optional API Key */}
                <div className="mb-3">
                    <label className="form-label text-muted" style={{ fontSize: "13px" }}>
                        API Key <span className="text-muted">(optional — uses default if empty)</span>
                    </label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={apiKeyPlaceholder}
                        value={apiKeyValue}
                        onChange={(e) => onApiKeyChange(e.target.value)}
                    />
                </div>

                <input
                    ref={fileInputRef}
                    className="form-control"
                    type="file"
                    accept={accept}
                    onChange={onFileChange}
                />
                <small className="text-muted d-block mt-1">{supportedText}</small>

                {/* Processing time badge */}
                {processingTime && (
                    <div className="mt-2">
                        <span className="badge bg-success">
                            Processed in {processingTime}s
                        </span>
                    </div>
                )}

                {/* Preview */}
                {previewImage && (
                    <div className="mt-3">
                        <p className="mb-2">Preview:</p>
                        <div style={{ border: "1px dashed #ddd", padding: "10px", borderRadius: "10px", textAlign: "center" }}>
                            <img
                                src={previewImage}
                                alt="preview"
                                style={{ maxWidth: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "8px" }}
                            />
                        </div>
                    </div>
                )}

                {/* Buttons */}
                <div className="mt-4 d-flex gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={onSubmit}
                        disabled={loading || !selectedFile}
                    >
                        {loading ? "Processing..." : "Remove Background"}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={onReset}
                        disabled={loading}
                    >
                        Reset
                    </button>
                </div>
            </div>
        </div>
    );

    // ─── Reusable Result Card ─────────────────────────────────────
    const ResultCard = ({ loading, resultImage, onDownload }) => (
        <div className="card custom-card h-100 shadow-sm border-0">
            <div className="card-body">
                <h6 className="mb-3">Result</h6>

                {loading && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" />
                        <p className="mt-3">Processing image...</p>
                    </div>
                )}

                {!loading && resultImage && (
                    <>
                        <div style={{
                            width: "100%", height: "420px", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            background: "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 0 0 / 20px 20px",
                            borderRadius: "12px", padding: "15px",
                        }}>
                            <img
                                src={resultImage}
                                alt="result"
                                style={{
                                    maxHeight: "380px", maxWidth: "100%",
                                    objectFit: "contain", borderRadius: "10px",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)"
                                }}
                            />
                        </div>
                        <div className="mt-3 text-center">
                            <button className="btn btn-success" onClick={onDownload}>
                                Download PNG
                            </button>
                        </div>
                    </>
                )}

                {!loading && !resultImage && (
                    <div
                        className="text-center d-flex align-items-center justify-content-center"
                        style={{ height: "420px", border: "2px dashed #ddd", borderRadius: "12px", color: "#aaa" }}
                    >
                        Result will appear here
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">

                {/* ── Section 1: Picsart ── */}
                <div className="card custom-card overflow-hidden border-0 shadow-sm mb-4">
                    <div className="card-body">
                        <h6 className="main-content-label mb-4">Background Removal Picsart</h6>
                        <div className="row g-4 align-items-stretch">
                            <div className="col-lg-6">
                                <UploadCard
                                    fileInputRef={fileInputRef}
                                    accept="image/*"
                                    supportedText="Supported: JPG, PNG, GIF, WEBP"
                                    apiKeyValue={picsartApiKey}
                                    apiKeyPlaceholder="paat-xxxxxxxxxxxxxxxx"
                                    onApiKeyChange={setPicsartApiKey}
                                    previewImage={previewImage}
                                    processingTime={processingTime}
                                    onFileChange={handleFileChange}
                                    onSubmit={handleSubmit}
                                    onReset={handleRemove}
                                    loading={loading}
                                    selectedFile={selectedFile}
                                />
                            </div>
                            <div className="col-lg-6">
                                <ResultCard
                                    loading={loading}
                                    resultImage={resultImage}
                                    onDownload={handleDownload}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Poof ── */}
                <div className="card custom-card overflow-hidden border-0 shadow-sm mb-4">
                    <div className="card-body">
                        <h6 className="main-content-label mb-4">Background Removal Poof</h6>
                        <div className="row g-4 align-items-stretch">
                            <div className="col-lg-6">
                                <UploadCard
                                    fileInputRef={poofFileInputRef}
                                    accept="image/jpeg,image/png,image/webp"
                                    supportedText="Supported: JPG, PNG, WEBP"
                                    apiKeyValue={poofApiKey}
                                    apiKeyPlaceholder="pk_xxxxxxxxxxxxxxxxxxxxxxxx"
                                    onApiKeyChange={setPoofApiKey}
                                    previewImage={poofPreviewImage}
                                    processingTime={poofProcessingTime}
                                    onFileChange={handlePoofFileChange}
                                    onSubmit={handlePoofSubmit}
                                    onReset={handlePoofRemove}
                                    loading={poofLoading}
                                    selectedFile={poofSelectedFile}
                                />
                            </div>
                            <div className="col-lg-6">
                                <ResultCard
                                    loading={poofLoading}
                                    resultImage={poofResultImage}
                                    onDownload={handlePoofDownload}
                                />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
};

export default BackgroundRemoval;